import { useState, useEffect, useRef } from 'react';
import { X, Loader2, Upload, Truck, FileText, Trash2, Plus, MapPin } from 'lucide-react';
import { cn, API_BASE_URL } from '../../lib/utils';
import { useAppSelector } from '../../store';
import { useSystemConfig } from '../../contexts/SystemConfigContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import type { C2Event, EventCategory } from '../../types';

type EventResource = { id: string; name: string; type: string; department: string };

type TabId = 'basics' | 'resources' | 'map' | 'uploads';

const tabs: { id: TabId; label: string }[] = [
  { id: 'basics', label: 'Event Basics' },
  { id: 'resources', label: 'Resources' },
  { id: 'map', label: 'Map' },
  { id: 'uploads', label: 'Uploads' },
];

interface CreateEventModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (event: C2Event) => void;
}

export default function CreateEventModal({ open, onClose, onCreated }: CreateEventModalProps) {
  const { token } = useAppSelector((state) => state.auth);
  const { enabledEventCategories, enabledResourceTypes } = useSystemConfig();
  const categoryOptions = enabledEventCategories.map((c) => c.id as EventCategory);
  const resourceTypes = enabledResourceTypes.map((r) => r.id);
  const [mode, setMode] = useState<'detail' | 'quick'>('detail');
  const [activeTab, setActiveTab] = useState<TabId>('basics');
  const [creating, setCreating] = useState(false);

  // Event basics
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<EventCategory>('other');
  const [eventDate, setEventDate] = useState(new Date().toISOString().split('T')[0]);
  const [eventStartTime, setEventStartTime] = useState(
    new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  );
  const [address, setAddress] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);
  const locationMarkerRef = useRef<L.Marker | null>(null);

  // Resources (created per-event)
  const [eventResources, setEventResources] = useState<EventResource[]>([]);
  const [newResName, setNewResName] = useState('');
  const [newResType, setNewResType] = useState('vehicle');
  const [newResDept, setNewResDept] = useState('');

  // Uploads (mock - files stored as names only in this demo)
  const [uploads, setUploads] = useState<{ id: string; name: string; size: number }[]>([]);

  // Map ref
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());

  // Clear drawings and location when modal opens (fresh state per creation)
  useEffect(() => {
    if (open) {
      drawnItemsRef.current.clearLayers();
      setSelectedLocation(null);
      if (locationMarkerRef.current) {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.removeLayer(locationMarkerRef.current);
        }
        locationMarkerRef.current = null;
      }
    }
  }, [open]);

  // Initialize map when tab switches to map
  useEffect(() => {
    if (activeTab !== 'map' || !mapContainerRef.current || mapInstanceRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [51.505, -0.09],
      zoom: 13,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    drawnItemsRef.current.addTo(map);
    mapInstanceRef.current = map;

    // Add draw controls
    const drawControl = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: { shapeOptions: { color: '#3b82f6', fillOpacity: 0.1, weight: 2 } },
        polyline: { shapeOptions: { color: '#3b82f6', weight: 2 } },
        rectangle: { shapeOptions: { color: '#3b82f6', fillOpacity: 0.1, weight: 2 } },
        circle: { shapeOptions: { color: '#3b82f6', fillOpacity: 0.1, weight: 2 } },
        marker: true,
        circlemarker: false,
      } as any,
      edit: { featureGroup: drawnItemsRef.current },
    });
    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, (e: any) => {
      drawnItemsRef.current.addLayer(e.layer);
    });

    // Handle location selection
    const handleMapClick = (e: L.LeafletMouseEvent) => {
      if (isSelectingLocation) {
        const { lat, lng } = e.latlng;
        setSelectedLocation({ lat, lng });

        // Remove existing marker
        if (locationMarkerRef.current) {
          map.removeLayer(locationMarkerRef.current);
        }

        // Add new marker
        const marker = L.marker([lat, lng], {
          icon: L.divIcon({
            className: 'custom-location-marker',
            html: `<div style="width:20px;height:20px;background:#ef4444;border:3px solid #fff;border-radius:50%;box-shadow:0 0 10px rgba(239,68,68,0.5);"></div>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10],
          }),
        }).addTo(map);
        locationMarkerRef.current = marker;

        setIsSelectingLocation(false);
        setActiveTab('basics');
      }
    };

    map.on('click', handleMapClick);

    const invalidateTimer = setTimeout(() => {
      if (mapInstanceRef.current === map) map.invalidateSize();
    }, 200);

    return () => {
      clearTimeout(invalidateTimer);
      map.off('click', handleMapClick);
      try {
        map.remove();
      } catch {
        // map may already be partially disposed (StrictMode double-invoke)
      }
      if (mapInstanceRef.current === map) {
        mapInstanceRef.current = null;
      }
    };
  }, [activeTab, isSelectingLocation]);

  // Resize map when it becomes visible
  useEffect(() => {
    if (activeTab === 'map' && mapInstanceRef.current) {
      setTimeout(() => mapInstanceRef.current?.invalidateSize(), 100);
    }
  }, [activeTab]);

  const reset = () => {
    setTitle('');
    setDescription('');
    setCategory('other');
    setEventDate(new Date().toISOString().split('T')[0]);
    setEventStartTime(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    setAddress('');
    setSelectedLocation(null);
    setIsSelectingLocation(false);
    setEventResources([]);
    setNewResName('');
    setNewResType('vehicle');
    setNewResDept('');
    setUploads([]);
    setActiveTab('basics');
    setMode('detail');
    drawnItemsRef.current.clearLayers();
    if (locationMarkerRef.current) {
      locationMarkerRef.current = null;
    }
    // Do NOT remove the map here — the useEffect cleanup owns that lifecycle.
    // Removing it manually causes a double-remove when the modal unmounts.
  };

  const handleCreate = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      // Create the event first
      const res = await fetch(`${API_BASE_URL}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: title,
          description,
          category,
          eventDate,
          eventStartTime,
          location: selectedLocation
            ? { latitude: selectedLocation.lat, longitude: selectedLocation.lng, address: address || 'Selected on map' }
            : { latitude: 0, longitude: 0, address: address || 'TBD' },
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const createdEvent = data.event;

        // Save map drawings to event-specific localStorage
        const layers = drawnItemsRef.current.getLayers();
        if (layers.length > 0) {
          const serializedDrawings = layers.map((layer: any) => {
            let serialized: any = {
              id: `draw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              type: 'unknown',
            };

            if (layer instanceof L.Marker) {
              serialized.type = 'marker';
              serialized.latlng = layer.getLatLng();
            } else if (layer instanceof L.Circle) {
              serialized.type = 'circle';
              serialized.latlng = layer.getLatLng();
              serialized.radius = layer.getRadius();
            } else if (layer instanceof L.Polyline) {
              serialized.type = 'polyline';
              serialized.latlngs = layer.getLatLngs();
            } else if (layer instanceof L.Polygon) {
              serialized.type = 'polygon';
              serialized.latlngs = layer.getLatLngs();
            }

            return serialized;
          });

          const storageKey = `c2_map_drawings_${createdEvent.id}`;
          localStorage.setItem(storageKey, JSON.stringify(serializedDrawings));
        }

        // Create resources for this event
        if (eventResources.length > 0) {
          await Promise.all(
            eventResources.map(resource =>
              fetch(`${API_BASE_URL}/resources`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  eventId: createdEvent.id,
                  name: resource.name,
                  type: resource.type,
                  department: resource.department,
                }),
              })
            )
          );
        }

        onCreated?.(createdEvent);
        reset();
        onClose();
      }
    } catch { /* ignore */ } finally {
      setCreating(false);
    }
  };

  const handleQuickCreate = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: title,
          description: description || title,
          category,
          eventDate,
          eventStartTime,
          location: { latitude: 0, longitude: 0, address: address || 'TBD' },
        }),
      });
      if (res.ok) {
        const data = await res.json();
        onCreated?.(data.event);
        reset();
        onClose();
      }
    } catch { /* ignore */ } finally {
      setCreating(false);
    }
  };

  const addResource = () => {
    if (!newResName.trim()) return;
    setEventResources((prev) => [
      ...prev,
      { id: `res-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: newResName.trim(), type: newResType, department: newResDept.trim() || 'General' },
    ]);
    setNewResName('');
    setNewResDept('');
  };

  const removeResource = (id: string) => {
    setEventResources((prev) => prev.filter((r) => r.id !== id));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newUploads = Array.from(files).map((f) => ({
      id: `upload-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: f.name,
      size: f.size,
    }));
    setUploads((prev) => [...prev, ...newUploads]);
    e.target.value = '';
  };

  const removeUpload = (id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };


  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl bg-[#0f1526] border border-white/[0.08] rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-white">Create event</h2>
            <p className="text-xs text-white/30 mt-0.5">Set up a new operational event with resources, maps, and documents.</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Mode toggle */}
            <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-0.5">
              <button
                onClick={() => setMode('detail')}
                className={cn(
                  'px-3 py-1.5 rounded-md text-xs font-semibold transition-all',
                  mode === 'detail'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-white/40 hover:text-white/60'
                )}
              >
                Detail Create
              </button>
              <button
                onClick={() => setMode('quick')}
                className={cn(
                  'px-3 py-1.5 rounded-md text-xs font-semibold transition-all',
                  mode === 'quick'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-white/40 hover:text-white/60'
                )}
              >
                Quick Create
              </button>
            </div>
            <button onClick={() => { reset(); onClose(); }} className="p-1.5 rounded-lg hover:bg-white/[0.06] transition-colors">
              <X className="w-4 h-4 text-white/40" />
            </button>
          </div>
        </div>

        {/* Tabs (Detail mode only) */}
        {mode === 'detail' && (
          <div className="flex gap-1 px-6 pt-3 border-b border-white/[0.06] flex-shrink-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-4 py-2 text-xs font-semibold rounded-t-lg transition-all relative',
                  activeTab === tab.id
                    ? 'text-primary bg-white/[0.04]'
                    : 'text-white/40 hover:text-white/60'
                )}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-primary rounded-full" />
                )}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">
          {mode === 'quick' ? (
            /* ── Quick Create ── */
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Name</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                  placeholder="e.g. Summer Music Festival 2025"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Date</label>
                  <input
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Start Time</label>
                  <input
                    type="time"
                    value={eventStartTime}
                    onChange={(e) => setEventStartTime(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                  />
                </div>
              </div>
            </div>
          ) : activeTab === 'basics' ? (
            /* ── Event Basics Tab ── */
            <div className="space-y-4">
              <div className="p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] space-y-4">
                <div>
                  <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Name</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                    placeholder="e.g. Summer Music Festival 2025"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Date</label>
                    <input
                      type="date"
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Event Start Time</label>
                    <input
                      type="time"
                      value={eventStartTime}
                      onChange={(e) => setEventStartTime(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30 resize-none"
                  placeholder="Describe the event..."
                />
              </div>

              <div>
                <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as EventCategory)}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                >
                  {categoryOptions.map((c) => (
                    <option key={c} value={c} className="bg-[#0f1526] text-white">
                      {c.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1.5">Location / Address</label>
                <div className="flex gap-2">
                  <input
                    value={selectedLocation ? `${selectedLocation.lat.toFixed(4)}, ${selectedLocation.lng.toFixed(4)}` : address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/30"
                    placeholder="Event address or location"
                    readOnly={!!selectedLocation}
                  />
                  <button
                    onClick={() => {
                      setIsSelectingLocation(true);
                      setActiveTab('map');
                    }}
                    className={cn(
                      'px-3 py-2.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5',
                      isSelectingLocation
                        ? 'bg-primary/20 text-primary border border-primary/30'
                        : 'bg-white/[0.04] text-white/60 border border-white/[0.08] hover:bg-white/[0.06] hover:text-white/80'
                    )}
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    Select on Map
                  </button>
                </div>
                {selectedLocation && (
                  <p className="text-[10px] text-primary mt-1.5">Location selected on map</p>
                )}
              </div>
            </div>
          ) : activeTab === 'resources' ? (
            /* ── Resources Tab ── */
            <div className="space-y-4">
              <p className="text-xs text-white/40">Add resources specific to this event. You can add more after the event is created.</p>

              {/* Add resource form */}
              <div className="p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1">Name</label>
                    <input
                      value={newResName}
                      onChange={(e) => setNewResName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addResource()}
                      className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-xs text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50"
                      placeholder="e.g. Engine-7"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1">Type</label>
                    <select
                      value={newResType}
                      onChange={(e) => setNewResType(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-xs text-white focus:outline-none focus:ring-1 focus:ring-primary/50"
                    >
                      {resourceTypes.map((t) => (
                        <option key={t} value={t} className="bg-[#0f1526] text-white">
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-white/50 uppercase tracking-wider block mb-1">Department</label>
                    <input
                      value={newResDept}
                      onChange={(e) => setNewResDept(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addResource()}
                      className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-xs text-white placeholder:text-white/20 focus:outline-none focus:ring-1 focus:ring-primary/50"
                      placeholder="e.g. Fire"
                    />
                  </div>
                </div>
                <button
                  onClick={addResource}
                  disabled={!newResName.trim()}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all',
                    newResName.trim()
                      ? 'bg-primary/15 text-primary border border-primary/20 hover:bg-primary/25'
                      : 'bg-white/[0.03] text-white/20 border border-white/[0.04] cursor-not-allowed'
                  )}
                >
                  <Plus className="w-3 h-3" /> Add Resource
                </button>
              </div>

              {/* Resource list */}
              {eventResources.length === 0 ? (
                <div className="text-center py-6 text-white/20 text-xs">No resources added yet</div>
              ) : (
                <div className="space-y-1.5">
                  {eventResources.map((res) => (
                    <div
                      key={res.id}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]"
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Truck className="w-4 h-4 text-primary/70" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-bold text-white/80">{res.name}</span>
                        <span className="text-[10px] text-white/30 ml-2">{res.department} · {res.type}</span>
                      </div>
                      <button
                        onClick={() => removeResource(res.id)}
                        className="p-1 rounded hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400/60" />
                      </button>
                    </div>
                  ))}
                  <p className="text-[10px] text-primary font-semibold">
                    {eventResources.length} resource{eventResources.length !== 1 ? 's' : ''} added
                  </p>
                </div>
              )}
            </div>
          ) : activeTab === 'map' ? (
            /* ── Map Tab ── */
            <div className="space-y-3">
              {isSelectingLocation ? (
                <div className="p-3 rounded-xl bg-primary/10 border border-primary/30">
                  <p className="text-xs text-primary font-semibold">
                    Click anywhere on the map to set the event location
                  </p>
                </div>
              ) : (
                <p className="text-xs text-white/40">
                  Use the drawing tools to plan the event map. You can add markers, zones, routes, and areas.
                </p>
              )}
              <div
                ref={mapContainerRef}
                className="w-full h-[380px] rounded-xl overflow-hidden border border-white/[0.08]"
              />
            </div>
          ) : activeTab === 'uploads' ? (
            /* ── Uploads Tab ── */
            <div className="space-y-4">
              <p className="text-xs text-white/40">
                Upload documents, plans, and files for this event. Available for printing in the workspace.
              </p>
              {/* Upload area */}
              <label className="flex flex-col items-center justify-center gap-2 p-8 rounded-xl border-2 border-dashed border-white/[0.1] bg-white/[0.02] cursor-pointer hover:bg-white/[0.04] hover:border-white/[0.15] transition-colors">
                <Upload className="w-8 h-8 text-white/20" />
                <span className="text-xs text-white/40 font-medium">Click to upload files</span>
                <span className="text-[10px] text-white/20">PDF, DOC, images, spreadsheets, etc.</span>
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              {/* File list */}
              {uploads.length > 0 && (
                <div className="space-y-1.5">
                  {uploads.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]"
                    >
                      <FileText className="w-4 h-4 text-primary/60 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/80 font-medium truncate">{file.name}</p>
                        <p className="text-[10px] text-white/30">{formatFileSize(file.size)}</p>
                      </div>
                      <button
                        onClick={() => removeUpload(file.id)}
                        className="p-1 rounded hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400/60" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-white/[0.06] flex-shrink-0">
          <button
            onClick={mode === 'quick' ? handleQuickCreate : handleCreate}
            disabled={creating || !title.trim()}
            className={cn(
              'px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2',
              title.trim()
                ? 'bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98]'
                : 'bg-white/[0.04] text-white/20 cursor-not-allowed'
            )}
          >
            {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {creating ? 'Creating...' : 'Create event'}
          </button>
          <button
            onClick={() => { reset(); onClose(); }}
            className="px-5 py-2.5 rounded-xl text-sm font-medium text-white/40 bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.06] transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
